<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
    <div class="w-full max-w-md bg-gray-800 text-gray-300 rounded-lg p-5 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">

      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-700 pb-4">
        <div class="flex items-center gap-3">
          <img v-if="villager.normalFace" :src="villager.normalFace" class="object-cover w-12 h-12 rounded-md border-2 border-indigo-400" @error="handleImageError" />
          <div>
            <h1 class="text-lg font-semibold text-indigo-200">{{ villager.name }}</h1>
            <div v-if="villager.level" class="flex items-center gap-1 mt-1">
              <i v-for="i in villager.level" :key="i" class="text-xs fas fa-star text-yellow-400"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab Navigation -->
      <div class="flex border-b border-gray-700">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="px-4 py-2 text-sm font-medium transition-colors duration-200 flex items-center gap-2"
          :class="activeTab === tab.id
            ? 'text-indigo-200 border-b-2 border-indigo-700'
            : 'text-gray-400 hover:text-indigo-300'"
        >
          <i :class="tab.icon" class="text-lg"></i>
          <span class="hidden md:inline">{{ tab.name }}</span>
        </button>
      </div>

      <!-- Content -->
      <div class="overflow-y-auto max-h-96">
        <!-- Tab: Info (New) -->
        <div v-if="activeTab === 'info'" class="space-y-4">
          <!-- Weapon -->
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-2">อาวุธที่ใช้</h3>
            <div class="text-sm text-indigo-200">{{ villager.weapon || 'ไม่มีอาวุธ' }}</div>
          </section>

          <!-- Strong / Weak -->
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-3">อาวุธที่แพ้–ชนะ</h3>

            <p class="text-xs text-indigo-300 mb-1">อาวุธที่ชนะทาง</p>
            <div class="flex flex-wrap gap-2 mb-3">
              <span v-for="weapon in villager.strongAgainst || []" :key="'s'+weapon"
                class="px-2 py-1 text-xs rounded-full bg-green-700 border border-green-500 text-green-100">{{ weapon }}</span>
              <span v-if="!(villager.strongAgainst && villager.strongAgainst.length)" class="text-xs text-indigo-300">— ไม่มีข้อมูล —</span>
            </div>

            <p class="text-xs text-indigo-300 mb-1">อาวุธที่แพ้ทาง</p>
            <div class="flex flex-wrap gap-2">
              <span v-for="weapon in villager.weakAgainst || []" :key="'w'+weapon"
                class="px-2 py-1 text-xs rounded-full bg-red-700 border border-red-500 text-red-100">{{ weapon }}</span>
              <span v-if="!(villager.weakAgainst && villager.weakAgainst.length)" class="text-xs text-indigo-300">— ไม่มีข้อมูล —</span>
            </div>
          </section>

          <!-- Locations -->
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-3">สถานที่ที่สามารถส่งไปได้</h3>
            <div class="flex flex-wrap gap-2">
              <span v-for="loc in villager.locations || []" :key="loc"
                class="px-2 py-1 text-xs rounded-full bg-indigo-700 border border-indigo-500 text-indigo-100">{{ loc }}</span>
              <span v-if="!(villager.locations && villager.locations.length)" class="text-xs text-indigo-300">— ไม่มีข้อมูล —</span>
            </div>
          </section>

          <!-- Perks -->
          <section v-if="villager.perks && villager.perks.length > 0" class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-2">Perks</h3>
            <ul class="space-y-2 text-sm text-indigo-200">
              <li v-for="perk in villager.perks" :key="perk">
                <strong>{{ perk }}</strong> - {{ getPerkDescription(perk) }}
              </li>
            </ul>
          </section>
        </div>

        <!-- Tab: Stats -->
        <div v-if="activeTab === 'stats'" class="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div v-for="stat in statOrder" :key="stat" class="stat-pill">
            <i :class="statMeta[stat]?.icon || 'fas fa-plus-circle'" class="text-xs"></i>
            <span class="text-2xs text-gray-400">{{ statMeta[stat]?.label || stat }}</span>
            <span class="font-semibold text-green-300">
              {{ totalStats[stat] }}
            </span>
          </div>
        </div>

        <!-- Tab: Powers -->
        <div v-if="activeTab === 'powers'" class="space-y-2">
          <div
            v-for="power in powerOrder"
            :key="power"
            class="flex justify-between items-center py-1 border-b border-gray-800 last:border-b-0"
          >
            <span class="text-sm text-indigo-200 flex items-center">
              <i :class="powerMeta[power]?.icon || 'fas fa-question-circle'" class="text-indigo-400 mr-2 text-sm"></i>
              {{ powerMeta[power]?.label || power }}
            </span>
            <span class="text-sm font-semibold text-white">
              {{ calculatedStats[power] }}{{ powerMeta[power]?.suffix || '' }}
            </span>
          </div>
        </div>

        <!-- Tab: Upgrade -->
        <div v-if="activeTab === 'upgrade'" class="space-y-4">
          <section class="bg-gray-700 border border-gray-600 p-4 rounded-md">
            <h3 class="text-sm font-semibold text-indigo-200 mb-3">ไอเท็มสำหรับอัปเกรด</h3>
            <div v-if="loadingUpgrade" class="flex justify-center py-4">
              <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
            </div>
            <div v-else-if="!upgradeItems.length" class="text-xs text-indigo-300">ไม่มีไอเท็มสำหรับอัปเกรด</div>
            <div v-else class="space-y-3">
              <div v-for="(item, index) in upgradeItems" :key="index" class="flex items-center gap-3 p-2 bg-gray-600 rounded-md">
                <img :src="item.img" class="w-10 h-10 rounded border border-gray-500 object-cover" @error="handleImageError" />
                <div class="flex-1">
                  <span class="text-sm text-indigo-200 font-medium">{{ item.name }}</span>
                  <span class="text-xs text-gray-400 ml-2">x {{ item.quantity }}</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <!-- Close Button -->
      <div class="flex justify-end">
        <button
          @click="$emit('close')"
          class="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 w-full rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group"
        >
          <i class="fas fa-times mr-1 text-sm group-hover:scale-110"></i> ปิด
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { projectFirestore } from '@/firebase/config';

export default {
  props: {
    villager: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup(props) {
    const activeTab = ref('info');
    const tabs = ref([
      { id: 'info', name: 'ข้อมูล', icon: 'fas fa-info-circle' },
      { id: 'stats', name: 'ค่าสเตตัส', icon: 'fas fa-chart-bar' },
      { id: 'powers', name: 'ค่าพลัง', icon: 'fas fa-bolt' },
      { id: 'upgrade', name: 'อัปเกรด', icon: 'fas fa-level-up-alt' }
    ]);

    const statOrder = ['stamina', 'str', 'int', 'agi', 'luck', 'defense'];
    const statMeta = {
      stamina: { label: 'Stamina', icon: 'fas fa-heart' },
      str: { label: 'STR', icon: 'fas fa-dumbbell' },
      int: { label: 'INT', icon: 'fas fa-brain' },
      agi: { label: 'AGI', icon: 'fas fa-running' },
      luck: { label: 'Luck', icon: 'fas fa-clover' },
      defense: { label: 'Defense', icon: 'fas fa-shield-alt' },
    };

    const powerOrder = ['maxHp', 'atk', 'critical', 'evasion', 'criticalReduction', 'dropRateBonus', 'damageReduction', 'defense'];
    const powerMeta = {
      maxHp: { label: 'พลังชีวิตสูงสุด', icon: 'fas fa-heartbeat' },
      atk: { label: 'พลังโจมตี', icon: 'fas fa-fist-raised' },
      critical: { label: 'คริติคอล', icon: 'fas fa-crosshairs', suffix: '%' },
      evasion: { label: 'หลบหลีก', icon: 'fas fa-running', suffix: '%' },
      criticalReduction: { label: 'ลดคริติคอล', icon: 'fas fa-shield-virus', suffix: '%' },
      dropRateBonus: { label: 'โบนัสดรอป', icon: 'fas fa-gift', suffix: '%' },
      damageReduction: { label: 'ลดดาเมจ', icon: 'fa-solid fa-person-rays', suffix: '%' },
      defense: { label: 'พลังป้องกัน', icon: 'fas fa-shield-alt' },
    };

    // Available perks list for descriptions
    const availablePerks = ref([
      { name: 'ผู้รอดชีวิต', description: 'ถ้าถูกฆ่าตายมีโอกาสกลับไปที่แคมป์ (โอกาส 50%)' },
      { name: 'บ้าคลั่ง', description: 'เมื่อมีเลือดต่ำกว่า 50 % มีโอกาเพิ่มลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'นักวางกับดัก', description: 'เมื่ออยู่ฝ่ายป้องกันมีโอกาสเพิ่มลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'นักบุกโจมตี', description: 'เมื่ออยู่ฝ่ายโจมตีมีโอกาสเพิ่มลูกเต๋า 1 โอกาส (โอกาส 20%)' },
      { name: 'อึดถึกทน', description: 'ลดดาเมจที่ได้รับ 20% (โอกาส 30%)' },
      { name: 'ฟื้นตัวไว', description: 'รักษาเลือด 10% max HP หลังรอบ (โอกาส 10%)' },
      { name: 'หลอกล่อ', description: 'มีโอกาสทำให้อีกฝ่ายลดลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'ผู้มีบุญ', description: 'เมื่อ hp เหลือ 0 มีโอกาสที่จะไม่ตายและกลับมามีเลือด 20% (โอกาส 10%)' },
      { name: 'หัวหน้าทีม', description: 'Heal ทีม 10 % max HP หลังรอบ (โอกาส 10%)' },
      { name: 'ฆาตกรโรคจิต', description: 'มีโอกาส +2 dice ถ้าทีมเหลือ 2 คน (โอกาส 20%)' },
      { name: 'นักเจรจา', description: 'เมื่อทอยลูกเต๋าแพ้มีโอกาสที่จะไม่โดนโจมตีเลย (โอกาส 10%)' },
      { name: 'ผู้ใช้อาวุธระยะไกล', description: '+10% crit ถ้าแต้มลูกเต๋านำมากว่า 3 แต้ม (โอกาส 100%)' }
    ]);

    const getBaseStats = () => props.villager.stats || props.villager.template?.stats || {};
    const getUpgradeStats = () => props.villager.upgradeStats || {};

    const totalStats = computed(() => {
      const base = getBaseStats();
      const upgrade = getUpgradeStats();
      return {
        stamina: (base.stamina || 0) + (upgrade.stamina || 0),
        str: (base.str || 0) + (upgrade.str || 0),
        int: (base.int || 0) + (upgrade.int || 0),
        agi: (base.agi || 0) + (upgrade.agi || 0),
        luck: (base.luck || 0) + (upgrade.luck || 0),
        defense: (base.defense || 0) + (upgrade.defense || 0),
        maxHp: (base.maxHp || 0) + (upgrade.maxHp || 0),
        atk: (base.atk || 0) + (upgrade.atk || 0),
        evasion: (base.evasion || 0) + (upgrade.evasion || 0),
        critical: (base.critical || 0) + (upgrade.critical || 0),
      };
    });

    const calculatedStats = computed(() => {
      const stats = totalStats.value;
      const defenseBonus = Math.floor(stats.defense / 20);
      return {
        atk: stats.str * 2 + stats.atk,
        critical: Number((Math.floor((stats.int / 2) + stats.critical)).toFixed(2)),
        maxHp: stats.stamina * 20 + stats.maxHp,
        evasion: Number((Math.floor((stats.agi / 2) + stats.evasion)).toFixed(2)),
        defense: stats.defense,
        criticalReduction: Number((Math.floor(stats.luck) + defenseBonus).toFixed(2)),
        dropRateBonus: Number((stats.luck * 0.01).toFixed(2)),
        damageReduction: Number(defenseBonus.toFixed(2))
      };
    });

    const getPerkDescription = (perkName) => {
      const perk = availablePerks.value.find(p => p.name === perkName);
      return perk ? perk.description : 'ไม่มีคำอธิบาย';
    };

    const upgradeItems = ref([]);
    const loadingUpgrade = ref(false);

    const fetchUpgradeItems = async () => {
      loadingUpgrade.value = true;
      try {
        const snap = await projectFirestore.collection('gameVillagers').where('name', '==', props.villager.name).get();
        if (snap.empty) {
          upgradeItems.value = [];
          return;
        }
        const templateData = snap.docs[0].data();
        if (templateData.upgradeItemNames?.length) {
          const quantity = templateData.upgradeQuantity || 1;
          const promises = templateData.upgradeItemNames.map(async (itemName) => {
            const itemSnap = await projectFirestore.collection('gameItems').doc(itemName).get();
            return {
              name: itemName,
              quantity: quantity,
              img: itemSnap.data()?.img || 'https://via.placeholder.com/64',
            };
          });
          upgradeItems.value = await Promise.all(promises);
        } else {
          upgradeItems.value = [];
        }
      } catch (err) {
        console.error('Error fetching upgrade items:', err);
        upgradeItems.value = [];
      } finally {
        loadingUpgrade.value = false;
      }
    };

    const handleImageError = (e) => {
      e.target.src = 'https://via.placeholder.com/64';
    };

    onMounted(async () => {
      await fetchUpgradeItems();
    });

    return {
      activeTab,
      tabs,
      statOrder,
      statMeta,
      powerOrder,
      powerMeta,
      totalStats,
      calculatedStats,
      getPerkDescription,
      villager: props.villager,
      upgradeItems,
      loadingUpgrade,
      handleImageError
    };
  }
};
</script>

<style scoped>
.max-h-96 { max-height: 24rem; }
.overflow-y-auto { overflow-y: auto; }
/* Animations */
.animate-spin { animation: spin 1s linear infinite; }
.animate-spin-pulse { animation: spin-pulse 1.2s ease-in-out infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.3s ease-out; }
@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }
/* Scrollbar */
.overflow-y-auto::-webkit-scrollbar { width: 5px; }
.overflow-y-auto::-webkit-scrollbar-track { background: #374151; border-radius: 10px; }
.overflow-y-auto::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 10px; }
.overflow-y-auto::-webkit-scrollbar-thumb:hover { background: #6b7280; }
/* Buttons */
button:disabled { opacity: 0.6; cursor: not-allowed; }
button:not(:disabled):active { transform: scale(0.98); }
/* Stat Pills */
.stat-pill {
  display: flex; align-items: center; justify-content: space-between; gap: 0.5rem;
  padding: 0.25rem 0.5rem; border-radius: 0.375rem;
  border: 1px solid #374151; /* gray-700 */
  background: #111827; /* gray-900 */
}
/* text-2xs for Tailwind 2.x */
.text-2xs { font-size: 0.7rem; line-height: 0.95rem; }
</style>